import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { DIAGNOSE, TOUR } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { GuidedTour } from "@/components/GuidedTour";
import { RecoveryTracker } from "@/components/RecoveryTracker";
import { DIAGNOSE_TOUR } from "@/lib/tours";
import { fmtSGD } from "@/lib/format";
import { AlertTriangle, Plus, ArrowUpRight, CheckCircle2, ListChecks, FileWarning, Info, HelpCircle } from "lucide-react";
import { toast } from "sonner";

export default function DiagnoseWorkspace() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const siteId = ws.siteId;
  const zoneId = ws.zoneId;
  const canEdit = hasRole("tenant_admin", "contract_manager", "supervisor");
  const [defects, setDefects] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [dOpen, setDOpen] = useState(false);
  const [iOpen, setIOpen] = useState(false);
  const [dForm, setDForm] = useState({});
  const [iForm, setIForm] = useState({});
  const [sp] = useSearchParams();
  const [tourOpen, setTourOpen] = useState(false);
  const startTour = () => { setDOpen(true); setTimeout(() => setTourOpen(true), 220); };
  useEffect(() => { if (sp.get("tour") === "diagnose") startTour(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!siteId) return;
    const [d, i] = await Promise.all([
      api.get(`/defects?site_id=${siteId}`),
      api.get(`/interventions?site_id=${siteId}`),
    ]);
    setDefects(d.data);
    setInterventions(i.data);
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const saveDefect = async () => {
    try {
      await api.post("/defects", { site_id: siteId, title: dForm.title, description: dForm.description, kind: dForm.kind || "recurring_defect", severity: dForm.severity || "medium" });
      toast.success("Logged"); setDOpen(false); setDForm({}); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveIntervention = async () => {
    try {
      await api.post("/interventions", { site_id: siteId, title: iForm.title, description: iForm.description, category: iForm.category || "repair", priority_score: Number(iForm.priority_score || 50), est_cost_sgd: iForm.est_cost_sgd ? Number(iForm.est_cost_sgd) : null, defect_id: iForm.defect_id === "none" ? null : iForm.defect_id });
      toast.success("Added to queue"); setIOpen(false); setIForm({}); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const approve = async (id) => {
    try { await api.patch(`/interventions/${id}`, { status: "approved" }); toast.success("Approved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const raiseWO = async (it) => {
    try {
      await api.post("/work-orders", { title: it.title, description: it.description, site_id: siteId, contract_id: ws.contractId || null, intervention_id: it.id, status: "approved", priority: "high" });
      toast.success("Work order raised", { description: "Find it in the Work Orders tab." });
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!siteId) return (
    <div className="space-y-5">
      <RecoveryTracker contract={ws.contract} />
      <p className="text-sm text-muted-foreground">Select a site to diagnose.</p>
    </div>
  );

  const defectZone = Object.fromEntries(defects.map((d) => [d.id, d.zone_id]));
  const vDefects = zoneId ? defects.filter((d) => d.zone_id === zoneId) : defects;
  const vInterventions = zoneId
    ? interventions.filter((it) => it.defect_id && defectZone[it.defect_id] === zoneId)
    : interventions;

  return (
    <div className="space-y-5">
      <RecoveryTracker contract={ws.contract} />
      {zoneId && (
        <p className="flex items-center gap-1.5 rounded-lg border border-sage-light bg-sage-surface px-3 py-1.5 text-xs text-forest-dark" data-testid="diagnose-zone-focus">
          <Info className="h-3.5 w-3.5 text-forest-mid" /> Focused on zone <b>{ws.zone?.name}</b> — showing its defects &amp; interventions. Switch to “All zones” in the top bar to see the whole site.
        </p>
      )}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      {/* site context */}
      <Card className="border-sage-light p-5">
        <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><Info className="h-4 w-4 text-forest-mid" /> Site context</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd className="font-medium capitalize">{(ws.site?.site_type || "").replace(/_/g, " ")}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Area</dt><dd className="font-medium">{ws.site?.area_sqm?.toLocaleString() || "—"} m²</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Location</dt><dd className="font-medium text-right">{ws.site?.location || "—"}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Open defects</dt><dd className="font-bold text-rose-600">{defects.filter((d) => d.status === "open").length}</dd></div>
          <div className="flex justify-between"><dt className="text-muted-foreground">Queued interventions</dt><dd className="font-bold text-forest-mid">{interventions.filter((i) => i.status !== "declined").length}</dd></div>
        </dl>
        {ws.site?.notes && <p className="mt-3 rounded-lg bg-sage-surface p-3 text-xs text-muted-foreground">{ws.site.notes}</p>}
      </Card>

      {/* defects */}
      <Card className="border-sage-light p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><FileWarning className="h-4 w-4 text-amber-600" /> Defects & gaps</h3>
          <div className="flex items-center gap-1.5">
            {canEdit && <Button size="sm" variant="ghost" onClick={startTour} data-testid={TOUR.startBtn("diagnose")} className="text-forest-mid"><HelpCircle className="mr-1 h-3.5 w-3.5" /> Tour</Button>}
            {canEdit && <Button size="sm" variant="outline" onClick={() => setDOpen(true)} data-testid={DIAGNOSE.addDefect}><Plus className="mr-1 h-3.5 w-3.5" /> Log</Button>}
          </div>
        </div>
        <div className="space-y-2">
          {vDefects.length === 0 && <p className="text-sm text-muted-foreground">No defects logged{zoneId ? " in this zone" : ""}.</p>}
          {vDefects.map((d) => (
            <div key={d.id} className="rounded-lg border bg-white p-3" data-testid={DIAGNOSE.defectRow(d.id)}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-forest-dark">{d.title}</p>
                <StatusBadge status={d.severity} kind="severity" />
              </div>
              {d.description && <p className="mt-1 text-xs text-muted-foreground">{d.description}</p>}
              <div className="mt-2 flex items-center gap-2">
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{d.kind.replace(/_/g, " ")}</span>
                <StatusBadge status={d.status} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* intervention queue */}
      <Card className="border-sage-light p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-forest-dark"><ListChecks className="h-4 w-4 text-forest-mid" /> Ranked queue</h3>
          {canEdit && <Button size="sm" variant="outline" onClick={() => setIOpen(true)} data-testid={DIAGNOSE.addIntervention}><Plus className="mr-1 h-3.5 w-3.5" /> Propose</Button>}
        </div>
        <div className="space-y-2">
          {vInterventions.length === 0 && <p className="text-sm text-muted-foreground">No interventions proposed{zoneId ? " for this zone" : ""}.</p>}
          {vInterventions.map((it, idx) => (
            <div key={it.id} className="rounded-lg border bg-white p-3" data-testid={DIAGNOSE.interventionRow(it.id)}>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-forest-mid text-xs font-bold text-white">{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-forest-dark">{it.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>Priority <b className="font-mono text-forest-mid">{it.priority_score}</b></span>
                    <span>·</span><span>{fmtSGD(it.est_cost_sgd)}</span>
                    <StatusBadge status={it.status} />
                  </div>
                  {canEdit && (
                    <div className="mt-2 flex gap-2">
                      {it.status !== "approved" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => approve(it.id)} data-testid={DIAGNOSE.approveBtn(it.id)}><CheckCircle2 className="mr-1 h-3 w-3" /> Approve</Button>}
                      <Button size="sm" className="h-7 bg-forest-mid text-xs hover:bg-forest-dark" onClick={() => raiseWO(it)} data-testid={DIAGNOSE.raiseWoBtn(it.id)}><ArrowUpRight className="mr-1 h-3 w-3" /> Raise WO</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* defect dialog */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent onInteractOutside={(e) => { if (tourOpen) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (tourOpen) e.preventDefault(); }}>
          <DialogHeader><DialogTitle className="font-heading">Log defect / data gap</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={dForm.title || ""} onChange={(e) => setDForm({ ...dForm, title: e.target.value })} data-testid={DIAGNOSE.field("defect-title")} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={dForm.description || ""} onChange={(e) => setDForm({ ...dForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Kind</Label>
                <Select value={dForm.kind || "recurring_defect"} onValueChange={(v) => setDForm({ ...dForm, kind: v })}><SelectTrigger data-testid={DIAGNOSE.field("defect-kind")}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="recurring_defect">Recurring defect</SelectItem><SelectItem value="data_gap">Data gap</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Severity</Label>
                <Select value={dForm.severity || "medium"} onValueChange={(v) => setDForm({ ...dForm, severity: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDOpen(false)}>Cancel</Button><Button onClick={saveDefect} className="bg-forest-mid hover:bg-forest-dark" data-testid={DIAGNOSE.saveBtn}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* intervention dialog */}
      <Dialog open={iOpen} onOpenChange={setIOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Propose intervention</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={iForm.title || ""} onChange={(e) => setIForm({ ...iForm, title: e.target.value })} data-testid={DIAGNOSE.field("intervention-title")} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={iForm.description || ""} onChange={(e) => setIForm({ ...iForm, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Linked defect (optional)</Label>
              <Select value={iForm.defect_id || "none"} onValueChange={(v) => setIForm({ ...iForm, defect_id: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— none —</SelectItem>{defects.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={iForm.category || "repair"} onValueChange={(v) => setIForm({ ...iForm, category: v })}><SelectTrigger data-testid={DIAGNOSE.field("intervention-category")}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="do_nothing">Do nothing</SelectItem><SelectItem value="repair">Repair</SelectItem><SelectItem value="redesign">Redesign</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Priority</Label><Input type="number" value={iForm.priority_score ?? ""} onChange={(e) => setIForm({ ...iForm, priority_score: e.target.value })} data-testid={DIAGNOSE.field("intervention-priority")} /></div>
              <div className="space-y-1.5"><Label>Est. cost</Label><Input type="number" value={iForm.est_cost_sgd ?? ""} onChange={(e) => setIForm({ ...iForm, est_cost_sgd: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIOpen(false)}>Cancel</Button><Button onClick={saveIntervention} className="bg-forest-mid hover:bg-forest-dark">Add to queue</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <GuidedTour open={tourOpen} steps={DIAGNOSE_TOUR.steps} title="Diagnose tour" onClose={() => setTourOpen(false)} />
      </div>
    </div>
  );
}
